import { Formik, FormikProps } from 'formik'
import { Box, Text, useToast } from 'native-base'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client'

import { AuthContext } from '../../context/AuthContext'
import {
  CREATE_SUPPORT_CONVERSATION_FEEDBACK,
  CreateSupportConversationFeedbackMutation,
  CreateSupportConversationFeedbackMutationVariables,
  GET_SUPPORT_CONVERSATION_FEEDBACK,
  GetSupportConversationFeedbackQuery,
  GetSupportConversationFeedbackQueryVariables,
  LOGIN_WITH_TOKEN,
  LoginWithTokenMutation,
  LoginWithTokenMutationVariables,
  SupportConversationFeedbackInput,
} from '@consumer/graphql'
import { signInWithToken } from '../../utils/firebase'
import { useScreenSize } from '../../utils/displayUtils'
import { trackEvent } from '../../utils/analytics-v2'
import { logBreadcrumb } from '../../utils/monitorClient'

import { ToastAlert } from '../Instructions/Common/ToastAlert'
import StickySubmitButton from '../FeedbackForm/StickySubmitButton'
import AgentExperienceSection from './AgentExperienceSection'
import ConsentSection from './ConsentSection'
import SupportFeedbackHero from './SupportFeedbackHero'
import { INITIAL_SUPPORT_FEEDBACK_VALUES, SupportFeedbackFormValues } from './types'
import {
  buildSupportFeedbackPayload,
  clearSavedSupportFormValues,
  getSavedSupportFormValues,
  saveSupportFormValues,
  trackSupportFeedbackSubmission,
} from './utils'

/** Debounces form values to localStorage so in-progress feedback survives page refreshes. */
function FormAutoSave({ conversationId, values }: { conversationId: string; values: SupportFeedbackFormValues }) {
  useEffect(() => {
    const timeout = setTimeout(() => saveSupportFormValues(conversationId, values), 300)
    return () => clearTimeout(timeout)
  }, [conversationId, values])
  return null
}

/**
 * Post-support-conversation CSAT form, reached from the SMS link iris sends after a C–F
 * grade: `/#/support-feedback/:conversationId?token=<Moneta magic token>`. Handles the
 * token login, the already-submitted check, form auto-save, and submission.
 *
 * Deliberately NOT feature-gated: whether a survey link exists at all is gated upstream
 * in iris (the only place the experiment flag is evaluated) — anyone holding a link must
 * be able to complete it. Access control is the authenticated consumer plus the BFF's
 * conversation-ownership check.
 */
export default function SupportFeedbackFormPage() {
  const screenSize = useScreenSize()
  const isMobileScreen = screenSize.width < 800
  const { conversationId } = useParams<{ conversationId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { firebaseUser } = useContext(AuthContext)
  const { show: showToast, close: closeToast } = useToast()
  const [isSubmitted, setIsSubmitted] = useState(false)

  const token = searchParams.get('token')
  const formPath = `/support-feedback/${conversationId}`
  // Guards against exchanging the same token twice while the effect's deps settle
  // (firebaseUser flips during sign-in, before the redirect strips the token).
  const tokenExchangeStarted = useRef(false)

  // The token names the consumer iris sent the link to. If a *different* consumer is
  // already signed in on this browser, keeping that session would submit under the
  // wrong identity (and be rejected by the BFF's ownership check), so fall back to the
  // existing session only when the exchange itself fails.
  const continueWithExistingSessionOrLeave = useCallback(() => {
    if (firebaseUser) {
      logBreadcrumb('Support feedback token exchange failed; continuing with existing session', { conversationId })
      navigate(formPath, { replace: true })
    } else {
      navigate('/', { replace: true })
    }
  }, [conversationId, firebaseUser, formPath, navigate])

  const [loginWithTokenMutation, { loading: tokenLoading }] = useMutation<
    LoginWithTokenMutation,
    LoginWithTokenMutationVariables
  >(LOGIN_WITH_TOKEN, {
    errorPolicy: 'all',
    onCompleted: async data => {
      if (!data?.loginWithToken) {
        continueWithExistingSessionOrLeave()
        return
      }
      try {
        await signInWithToken(data.loginWithToken)
        navigate(formPath, { replace: true })
      } catch (error) {
        logBreadcrumb('Support feedback Firebase sign-in failed', { conversationId, error })
        continueWithExistingSessionOrLeave()
      }
    },
    onError: continueWithExistingSessionOrLeave,
  })

  // Authenticate with the token from the URL, or leave if there is neither a token nor a session.
  useEffect(() => {
    if (!conversationId) return

    if (token) {
      if (tokenExchangeStarted.current) return
      tokenExchangeStarted.current = true
      loginWithTokenMutation({ variables: { token } })
      return
    }

    if (!firebaseUser) {
      navigate('/', { replace: true })
    }
  }, [conversationId, token, firebaseUser, loginWithTokenMutation, navigate])

  const [createSupportConversationFeedback, { loading: isSubmittingFeedback }] = useMutation<
    CreateSupportConversationFeedbackMutation,
    CreateSupportConversationFeedbackMutationVariables
  >(CREATE_SUPPORT_CONVERSATION_FEEDBACK)

  // The write is a strict one-row-per-conversation create, so a customer re-tapping an
  // old SMS link would otherwise reach a form doomed to a duplicate error. Check up front
  // and show the already-submitted notice. Skipped until the token (if any) has been
  // exchanged, so the check runs as the consumer the link was sent to.
  const {
    data: existingFeedback,
    loading: existingFeedbackLoading,
    error: existingFeedbackError,
    refetch: refetchExistingFeedback,
  } = useQuery<GetSupportConversationFeedbackQuery, GetSupportConversationFeedbackQueryVariables>(
    GET_SUPPORT_CONVERSATION_FEEDBACK,
    {
      variables: { conversationId: conversationId ?? '' },
      skip: !firebaseUser || !conversationId || !!token,
      fetchPolicy: 'network-only',
    }
  )
  const alreadySubmitted = !!existingFeedback?.getSupportConversationFeedback

  useEffect(() => {
    if (existingFeedbackError) {
      // Not fatal: the submit path re-checks on failure, so a duplicate still ends in the notice.
      logBreadcrumb('Support feedback existing-submission check failed', {
        conversationId,
        error: existingFeedbackError,
      })
    }
  }, [conversationId, existingFeedbackError])

  useEffect(() => {
    if (firebaseUser && conversationId && !token) {
      trackEvent('support_feedback_form_viewed', { conversationId })
    }
  }, [firebaseUser, conversationId, token])

  const handleSubmit = useCallback(
    async (values: SupportFeedbackFormValues) => {
      if (!conversationId) return

      // No consumerId: the BFF derives it from the authenticated identity.
      const feedbackInput: SupportConversationFeedbackInput = {
        conversationId,
        feedback: JSON.stringify(buildSupportFeedbackPayload(values)),
      }

      try {
        const { data } = await createSupportConversationFeedback({ variables: { feedbackInput } })
        if (!data?.createSupportConversationFeedback) {
          throw new Error('createSupportConversationFeedback returned no feedback')
        }
        trackSupportFeedbackSubmission(values, conversationId)
        setIsSubmitted(true)
        clearSavedSupportFormValues(conversationId)
      } catch (error) {
        // A failed submit may be the duplicate-create guard (e.g. two tabs, or a submit
        // racing this page's load check) — re-check before assuming a transient error,
        // and let the already-submitted notice take over.
        try {
          const { data } = await refetchExistingFeedback()
          if (data?.getSupportConversationFeedback) {
            clearSavedSupportFormValues(conversationId)
            return
          }
        } catch {
          // fall through to the generic toast
        }
        logBreadcrumb('Support feedback submit failed', { conversationId, error })
        showToast({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title="Unable to Submit Feedback"
              description="Sorry, something went wrong. Please try again."
              status="error"
              variant="subtle"
              isClosable={true}
              closeToast={closeToast}
            />
          ),
          duration: 5000,
        })
      }
    },
    [conversationId, showToast, closeToast, createSupportConversationFeedback, refetchExistingFeedback]
  )

  // Loading state: token exchange in flight (or about to start), or the already-submitted check.
  if (token || tokenLoading || existingFeedbackLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={10}>
        <Text>Loading...</Text>
      </Box>
    )
  }

  // Already-submitted notice: the customer tapped an older SMS link (or a duplicate submit
  // was caught) — feedback for this conversation exists.
  if (alreadySubmitted && !isSubmitted) {
    return (
      <Box
        mx="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={isMobileScreen ? '100%' : '750px'}
        py={10}
        px={4}
        pb={0}
      >
        <Text fontSize={24} fontWeight="700" fontFamily="bodyBold" textAlign="center" mb={21}>
          You&apos;ve already shared your feedback
        </Text>
        <Text fontSize={14} fontFamily="Matter Regular" textAlign="center" color="#858581">
          Thanks — we received your feedback for this conversation. We&apos;re always here if you need us.
        </Text>
      </Box>
    )
  }

  // Success state
  if (isSubmitted) {
    return (
      <Box
        mx="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={isMobileScreen ? '100%' : '750px'}
        py={10}
        px={4}
        pb={0}
      >
        <Text fontSize={24} fontWeight="700" fontFamily="bodyBold" textAlign="center" mb={21}>
          Thank you for your feedback!
        </Text>
        <Text fontSize={14} fontFamily="Matter Regular" textAlign="center" color="#858581">
          Your responses help us improve the support experience for everyone.
        </Text>
      </Box>
    )
  }

  return (
    <Box
      mx="auto"
      display="flex"
      flexDirection="column"
      width={isMobileScreen ? '100%' : '750px'}
      backgroundColor="#F5F5ED"
    >
      <Formik<SupportFeedbackFormValues>
        initialValues={
          conversationId
            ? (getSavedSupportFormValues(conversationId) ?? INITIAL_SUPPORT_FEEDBACK_VALUES)
            : INITIAL_SUPPORT_FEEDBACK_VALUES
        }
        onSubmit={handleSubmit}
      >
        {(formikProps: FormikProps<SupportFeedbackFormValues>) => {
          const { handleSubmit: formikHandleSubmit, values } = formikProps

          const hasAnyAnswer =
            values.overallSupportExperience !== null ||
            values.supportComments.trim() !== '' ||
            values.consentToFollowUp === true

          return (
            <>
              {conversationId && <FormAutoSave conversationId={conversationId} values={values} />}
              <SupportFeedbackHero />

              <AgentExperienceSection formikProps={formikProps} />

              <ConsentSection formikProps={formikProps} />
              <StickySubmitButton
                onPress={() => formikHandleSubmit()}
                isLoading={isSubmittingFeedback}
                isDisabled={!hasAnyAnswer}
              />
            </>
          )
        }}
      </Formik>
    </Box>
  )
}
